/**
 * SizeGuideModal extends the functionality of the Modal class and includes TabControlMixin.
 * This modal provides a size guide with the capability to convert measurement units,
 * setup table values and highlight values on hover.
 */
class SizeGuideModal extends window.TabControlMixin(window.Modal) {
  isLoaded = false

  constructor() {
    super()
    // Default unit is 'cm' but can be overridden by dataset attributes
    this.baseUnit = this.dataset.baseUnit || 'cm'
    this.activeUnit = this.baseUnit
  }

  /**
   * Convert the given value to the currently active unit.
   * @param {number} value - The value to be converted.
   * @returns {number} - The converted value.
   */
  convertToActiveUnit(value) {
    return this.activeUnit === 'cm' ? value : value * 0.393701
  }

  /**
   * Setup the values within the table, converting them to the active unit.
   * @param {HTMLElement} table - The table element.
   */
  setupTableValues(table) {
    const cells = table.querySelectorAll('[data-base-value]')
    cells.forEach((cell) => {
      const value = parseFloat(cell.dataset.baseValue)
      if (!isNaN(value)) {
        const newValue = this.convertToActiveUnit(value)
        cell.textContent = parseFloat(newValue.toFixed(2))
      }
    })
  }

  /**
   * Highlight the cells based on the hovered cell.
   * @param {Event} e - The mouseover event.
   */
  hoverHighlight(e) {
    const parent = e.target.closest('td')
    if (parent) {
      const index = parent.cellIndex
      const rows = parent.closest('table').querySelectorAll('tr')
      rows.forEach((row) => {
        const cells = row.querySelectorAll('td, th')
        cells.forEach((cell, i) => {
          if (row.rowIndex === parent.closest('tr').rowIndex || i === index) {
            cell.classList.add('highlight')
          } else {
            cell.classList.remove('highlight')
          }
        })
      })
    }
  }

  /**
   * Setup the table to highlight cells on hover.
   */
  setupTableHover() {
    this.querySelectorAll('table').forEach((table) => {
      table.addEventListener('mouseover', this.hoverHighlight.bind(this))
      this.setupTableValues(table)
    })
  }

  /**
   * Setup the switcher for measurement units.
   */
  setupUnitSwitcher() {
    const unitSwitchers = this.querySelectorAll('[data-unit]')
    unitSwitchers.forEach((unitSwitcher) => {
      unitSwitcher.addEventListener('click', (e) => {
        e.preventDefault()
        const targetUnit = e.target.dataset.unit
        if (targetUnit !== this.activeUnit) {
          this.activeUnit = targetUnit
          unitSwitchers.forEach((switcher) => {
            switcher.classList.remove('active')
          })
          e.target.classList.add('active')
          this.querySelectorAll('table').forEach((table) => {
            this.setupTableValues(table)
          })
        }
      })
    })
  }

  /**
   * Open the size guide modal.
   * If the content isn't loaded, it fetches the content from the template, sets it up, and then displays it.
   */
  async open() {
    super.open()
    if (!this.isLoaded) {
      const template = this.querySelector('template')
      if (template) {
        const content = template.content.cloneNode(true)
        this.appendChild(content)
        this.setupTabs()
        this.setupTableHover()
        this.setupUnitSwitcher()
      }
      this.isLoaded = true
    }
  }
}

// Register the custom element 'size-guide-modal'
window.customElements.define('size-guide-modal', SizeGuideModal)
